import { AlertTriangle } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import { ChatInput } from './ChatInput';
import useAIChatStreamHandler from './hooks/useAIChatStreamHandler';
import MessageArea from './MessageArea';
import useStore from './store';

interface ChatInterfaceProps {
  onStartChat?: () => void;
  initialMessage?: string;
  isReady?: boolean;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ onStartChat, initialMessage, isReady }) => {
  const isConnected = useStore((s) => s.isEndpointActive);
  const effectiveIsReady = isReady ?? isConnected;
  const { handleStreamResponse } = useAIChatStreamHandler();
  const isStreaming = useStore((state) => state.isStreaming);

  const handleSend = useCallback(async (message: string | FormData) => {
    if (!message || isStreaming || !effectiveIsReady) return;
    onStartChat?.();

    try {
      await handleStreamResponse(message);
    } catch (error) {
      console.error('Error in handleSend:', error instanceof Error ? error.message : String(error));
    }
  }, [isStreaming, effectiveIsReady, handleStreamResponse, onStartChat]);

  const handleSendMessage = useCallback((message: string | FormData) => {
    handleSend(message).catch(console.error);
  }, [handleSend]);

  useEffect(() => {
    if (initialMessage && effectiveIsReady) {
      handleSend(initialMessage).catch(console.error);
    }
  }, [initialMessage, effectiveIsReady, handleSend]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      {/* Connection Error State */}
      {!effectiveIsReady && (
        <div className="flex-1 min-h-0 flex items-center justify-center pb-24">
          <div className="text-center py-12">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-destructive/30" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Connection Error
            </h3>
            <p className="text-sm text-muted-foreground">
              Unable to connect to AgentOS. Please check your connection.
            </p>
          </div>
        </div>
      )}

      {/* Messages Area */}
      {effectiveIsReady && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <MessageArea />
        </div>
      )}

      {/* Chat Input at Bottom */}
      <div className="flex-shrink-0 border-t border-border">
        <ChatInput
          onSendMessage={handleSendMessage}
          isReady={effectiveIsReady}
          isStreaming={isStreaming}
          placeholder="Ask about your accounts, create reports, draft emails..."
        />
      </div>
    </div>
  );
};

export default ChatInterface;
